import React from "react";
import { motion } from "framer-motion";
import {
  Brain,
  Cloud,
  Cpu,
  Code2,
  Database,
  LayoutGrid,
  Users,
} from "lucide-react";
import { skills } from "../data/portfolio";

const ease = [0.16, 1, 0.3, 1];

const iconMap = {
  "Programming & Frameworks": Code2,
  "AI Agents & LLM Engineering": Brain,
  "RAG, Vector Search & Embeddings": Database,
  "Workflow Automation & Integration": Cpu,
  "Backend & APIs": LayoutGrid,
  "Cloud, Containers & DevOps": Cloud,
  "Data & Storage": Database,
  "Client Delivery & Methodologies": Users,
};

// Marquee pulls every skill keyword from portfolio data (deduped, stable order)
const marqueeTools = [
  ...new Set(skills.flatMap((cat) => cat.items)),
];

function Marquee({ items, reverse }) {
  // duplicate items to enable seamless loop
  const doubled = [...items, ...items];
  return (
    <div className="relative overflow-hidden py-3 [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
      <div
        className="marquee-track flex gap-3 w-max"
        style={{
          animationDuration: "60s",
          animationDirection: reverse ? "reverse" : "normal",
        }}
      >
        {doubled.map((t, i) => (
          <span
            key={`${t}-${i}`}
            className="inline-flex items-center px-4 h-9 rounded-full border border-line/10 bg-line/[0.03] font-body text-sm text-ink-metal whitespace-nowrap"
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

// asymmetric bento spans (8 categories)
const spanMap = {
  0: "md:col-span-5",
  1: "md:col-span-7 md:row-span-2", // AI Agents — hero
  2: "md:col-span-5",
  3: "md:col-span-6",
  4: "md:col-span-6",
  5: "md:col-span-7 md:row-span-2", // Cloud — hero
  6: "md:col-span-5",
  7: "md:col-span-12",
};

function SkillCard({ cat, idx, span }) {
  const Icon = iconMap[cat.category] || LayoutGrid;
  const isHero =
    cat.category === "AI Agents & LLM Engineering" ||
    cat.category === "Cloud, Containers & DevOps";

  return (
    <motion.div
      data-testid={`skill-card-${idx}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease, delay: (idx % 4) * 0.06 }}
      whileHover={{ y: -3 }}
      className={`group relative col-span-1 ${span} p-7 md:p-9 rounded-2xl border border-line/5 bg-surface-3/60 hover:border-line/20 hover:bg-surface-3 transition-all duration-500 overflow-hidden`}
    >
      {/* subtle gradient on hover */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgb(var(--ink)/0.04),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div
          className={`grid place-items-center rounded-xl ${
            isHero ? "w-12 h-12 bg-ink text-surface" : "w-10 h-10 bg-line/5 text-ink-metal"
          } transition-all duration-500`}
        >
          <Icon
            className={isHero ? "w-5 h-5" : "w-4.5 h-4.5"}
            strokeWidth={isHero ? 1.6 : 1.4}
          />
        </div>
        <span className="font-body text-[10px] tracking-[0.3em] uppercase text-ink-3">
          {String(idx + 1).padStart(2, "0")}
        </span>
      </div>

      <h3
        className={`font-display tracking-[-0.02em] text-ink mb-5 ${
          isHero ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"
        }`}
      >
        {cat.category}
      </h3>

      {isHero && cat.category === "AI Agents & LLM Engineering" && (
        <p className="font-body text-sm text-ink-2 mb-5 max-w-md leading-relaxed">
          Production agents end-to-end — LangChain, LangGraph, MCP, RAG, and the
          LLM providers and coding tools I use daily to ship.
        </p>
      )}
      {isHero && cat.category === "Cloud, Containers & DevOps" && (
        <p className="font-body text-sm text-ink-2 mb-5 max-w-md leading-relaxed">
          Azure-first for enterprise AI — plus AWS, GCP, containers, and CI/CD
          pipelines that keep agents and apps running at scale.
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {cat.items.map((it) => (
          <span
            key={it}
            className={`inline-flex items-center px-2.5 h-7 rounded-full border border-line/5 font-body text-[12px] text-ink-metal hover:bg-line/[0.08] hover:border-line/15 hover:text-ink transition-colors duration-300 ${
              isHero ? "bg-line/[0.05]" : "bg-line/[0.03]"
            }`}
          >
            {it}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

export default function Skills() {
  return (
    <section
      id="skills"
      data-testid="skills-section"
      data-snap-page
      data-snap-overflow
      className="relative bg-surface-2 border-y border-line/5 h-[100svh]"
    >
      <div className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-3">
            <p className="font-body text-[10px] md:text-xs tracking-[0.4em] uppercase text-ink-2">
              05 — The Stack
            </p>
          </div>
          <div className="col-span-12 md:col-span-9 md:col-start-4 mt-6 md:mt-0">
            <h2 className="font-display text-4xl md:text-6xl tracking-[-0.03em] leading-[1.05] text-ink max-w-[22ch]">
              Tools I reach for, <span className="text-metal">on instinct.</span>
            </h2>
            <p className="mt-6 font-body text-base md:text-lg text-ink-2 max-w-2xl leading-relaxed">
              From AI-native IDEs to the Azure plumbing that runs the lights — a
              constantly evolving toolbox, curated by what ships.
            </p>
          </div>
        </div>
      </div>

        {/* Marquee — full-bleed */}
        <div className="mb-3 md:mb-5">
          <Marquee items={marqueeTools} />
        </div>
        <div>
          <Marquee items={marqueeTools.slice().reverse()} reverse />
        </div>
      </div>
    </section>
  );
}
